/**
 * **O volume do jogo: um ganho mestre, e mais nada** (gate V28G.N.1).
 *
 * ## Porque isto existe
 *
 * Porque o gate anterior pôs o PRIMEIRO som deste jogo - as garras e o corte da
 * Garra Hemorrágica - e deixou-o sem maneira de o baixar. Um jogo que faz
 * barulho e não tem volume não é um jogo com um efeito por afinar: é um jogo com
 * uma lacuna.
 *
 * ## O que isto NÃO é
 *
 * Não é uma mesa de mistura. Não há canais, não há categorias, não há volume de
 * música separado do de efeitos, e não há nada disso por vir a pedido deste
 * ficheiro. **Volume mestre e silêncio**, que é o que o dono pediu e o que um
 * jogo com dois sons precisa.
 *
 * ## Um contexto, um ganho, e é o do Phaser
 *
 * Este módulo **nunca cria um `AudioContext`**. O Phaser já cria exatamente um
 * por aba, e destranca-o no primeiro gesto do jogador; o que aqui se faz é
 * ADOTÁ-LO e pendurar-lhe um único `GainNode` entre os sons e as colunas.
 *
 * `attach` é idempotente: chamada dez vezes com o mesmo contexto, cria um ganho.
 * É o que garante que abrir e fechar o duelo e a caça - que constroem sistemas
 * de som novos de cada vez - não acumula nós ligados ao destino.
 *
 * ## O ganho local continua a existir
 *
 * `BLEED_AUDIO_GAIN` não desaparece nem se mistura aqui. Ele é o peso RELATIVO
 * daquele som dentro do jogo; este é o volume que o jogador escolheu. Os dois
 * multiplicam-se, e a 100 % o produto é exatamente o som aprovado - a
 * multiplicação por um é a identidade, e é por isso que esta fundação podia
 * entrar sem voltar a tocar no que já estava aprovado.
 */

/** O que o jogador escolheu. Duas coisas, e nunca mais do que duas. */
export interface AudioSettings {
  /** De 0 a 1. Guardado mesmo enquanto o som está silenciado. */
  readonly volume: number;
  readonly muted: boolean;
}

/**
 * **Volume a 100 %, sem silêncio.**
 *
 * Um jogo novo faz som. O contrário - abrir calado e obrigar a procurar onde se
 * liga - esconde a existência do áudio de quem nunca leu isto.
 */
export const AUDIO_DEFAULTS: AudioSettings = { volume: 1, muted: false };

/**
 * A chave onde as duas coisas ficam.
 *
 * Versionada: se um dia a forma guardada mudar, uma chave nova é a diferença
 * entre ignorar o que estava lá e interpretá-lo mal.
 */
export const AUDIO_STORAGE_KEY = 'kenocyber:audio:v1';

/** O mínimo de `localStorage` que este módulo escreve. */
export interface AudioStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** E o mínimo do contexto: saber fazer um ganho, e ter para onde o ligar. */
export interface MasterAudioContext {
  createGain(): GainNode;
  readonly destination: AudioNode;
}

/** Um volume utilizável, venha de onde vier. Fora de `[0, 1]` é aparado. */
function clampVolume(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(numeric)) {
    return AUDIO_DEFAULTS.volume;
  }
  return Math.min(1, Math.max(0, numeric));
}

/**
 * **O que sai**, dado o que o jogador escolheu.
 *
 * Silenciado é zero, e o volume por baixo dele fica INTACTO - é isso que faz
 * dessilenciar devolver o que lá estava em vez de um valor por omissão.
 */
export function audioOutputGain(settings: AudioSettings): number {
  return settings.muted ? 0 : clampVolume(settings.volume);
}

/**
 * Lê o que estava guardado, ou o que está por omissão.
 *
 * Nunca lança. Um `localStorage` inacessível - janela privada, dados bloqueados,
 * uma captura de miniatura - é um caso ordinário, e a resposta certa é o padrão
 * e não um ecrã partido.
 */
export function readAudioSettings(storage: AudioStorage | undefined | null): AudioSettings {
  if (!storage) {
    return AUDIO_DEFAULTS;
  }
  try {
    const raw = storage.getItem(AUDIO_STORAGE_KEY);
    if (raw === null) {
      return AUDIO_DEFAULTS;
    }
    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      volume: clampVolume(parsed?.volume),
      muted: parsed?.muted === true,
    };
  } catch {
    return AUDIO_DEFAULTS;
  }
}

/** E escreve, com a mesma tolerância. */
function writeAudioSettings(storage: AudioStorage | undefined | null, settings: AudioSettings) {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(AUDIO_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /*
     * Guardar falhou, e o som continua a funcionar nesta sessão.
     *
     * Uma quota cheia ou um armazenamento bloqueado não são razão para o volume
     * deixar de responder: perde-se a memória entre recargas, não o controlo.
     */
  }
}

/**
 * **O dono do volume.** Um por aba, e o que o resto do jogo lhe pede é o nó.
 */
export class AudioMaster {
  private context: MasterAudioContext | null = null;
  private gain: GainNode | null = null;
  private current: AudioSettings;
  private readonly listeners = new Set<() => void>();
  /** Quantos contextos foram adotados. Uma prova lê isto; o jogo não. */
  private adopted = 0;

  constructor(private readonly storage: AudioStorage | undefined | null) {
    this.current = readAudioSettings(storage);
  }

  /**
   * Adota o contexto do Phaser e pendura-lhe UM ganho.
   *
   * Idempotente por contexto. Um contexto diferente - que só aconteceria se o
   * jogo inteiro fosse reconstruído - larga o ganho anterior antes de fazer o
   * novo, porque um nó ligado ao destino de um contexto morto é um nó a mais.
   */
  attach(context: MasterAudioContext): void {
    if (this.context === context && this.gain !== null) {
      return;
    }
    if (this.gain) {
      try {
        this.gain.disconnect();
      } catch {
        /* Um nó de um contexto já fechado. Largá-lo é tudo o que falta. */
      }
    }
    this.context = context;
    this.gain = context.createGain();
    this.gain.connect(context.destination);
    this.adopted += 1;
    this.apply();
  }

  /** Onde os sons se ligam, ou `null` enquanto não houver contexto nenhum. */
  output(): AudioNode | null {
    return this.gain;
  }

  /** Quantas vezes um contexto foi adotado. Deve ser um, e uma prova exige-o. */
  adoptions(): number {
    return this.adopted;
  }

  /** O que o jogador escolheu, agora. */
  settings(): AudioSettings {
    return this.current;
  }

  /** O ganho que está mesmo a sair. Zero quando silenciado. */
  outputGain(): number {
    return audioOutputGain(this.current);
  }

  setVolume(volume: number): void {
    this.update({ ...this.current, volume: clampVolume(volume) });
  }

  /**
   * Silencia ou devolve o som.
   *
   * **Não toca no volume.** Silenciar não é pôr o volume a zero: é uma segunda
   * coisa, por cima dele, e é isso que faz dessilenciar devolver exatamente o
   * que lá estava.
   */
  setMuted(muted: boolean): void {
    this.update({ ...this.current, muted });
  }

  toggleMuted(): void {
    this.setMuted(!this.current.muted);
  }

  /** Avisa quando alguma das duas coisas muda. Devolve como deixar de ouvir. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Quantos ouvintes estão registados. Uma prova de fuga lê isto. */
  listenerCount(): number {
    return this.listeners.size;
  }

  private update(next: AudioSettings): void {
    if (next.volume === this.current.volume && next.muted === this.current.muted) {
      return;
    }
    this.current = next;
    writeAudioSettings(this.storage, next);
    this.apply();
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  private apply(): void {
    if (this.gain) {
      this.gain.gain.value = this.outputGain();
    }
  }
}

/** O `localStorage` desta aba, ou nada quando ele não existe ou recusa. */
function browserStorage(): AudioStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * **O mestre desta aba.**
 *
 * Um só, partilhado pelos sons de hoje e pelos que vierem: um segundo mestre
 * seria um segundo volume, e o jogador só conhece um.
 */
export const audioMaster = new AudioMaster(browserStorage());
